import { ColTypeDef } from 'ag-grid-community';
import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { useClientSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useClientSideDefaultColumnTypes';
import { useMemo } from 'react';
import { useCustodianColumnType } from 'routes/secondary-market-batch/grid/hooks/use-custodian-column-type';
import { useDatesArrayColumnType } from 'routes/secondary-market-batch/grid/hooks/use-dates-array-column-type';
import { useDatetimeWithTimezoneColumnType } from 'routes/secondary-market-batch/grid/hooks/use-datetime-with-timezone-column-type';
import { useLabeledOptionListColumnType } from 'routes/secondary-market-batch/grid/hooks/use-labeled-option-list-column-type';
import { useLargeTextColumnType } from 'routes/secondary-market-batch/grid/hooks/use-large-text-column-type';
import { useNumberColumnType } from 'routes/secondary-market-batch/grid/hooks/use-number-column-type';
import { useOperationStatus } from 'routes/secondary-market-batch/grid/hooks/use-operation-status';
import { usePercentOrAmountColumnType } from 'routes/secondary-market-batch/grid/hooks/use-percent-or-amount-column-type';
import { useRestrictedDateColumnType } from 'routes/secondary-market-batch/grid/hooks/use-restricted-date-column-type';
import { useSalesColumnType } from 'routes/secondary-market-batch/grid/hooks/use-sales-column-type';
import { useSalesCounterpartColumnType } from 'routes/secondary-market-batch/grid/hooks/use-sales-counterpart-column-type';
import { useSemanticDatetimeWithTimezoneColumnType } from 'routes/secondary-market-batch/grid/hooks/use-semantic-datetime-with-timezone-column-type';
import { useSettlementsColumnType } from 'routes/secondary-market-batch/grid/hooks/use-settlements-column-type';
import { useStringArrayColumnType } from 'routes/secondary-market-batch/grid/hooks/use-string-array-column-type';
import { useUpfrontFeeColumnType } from 'routes/secondary-market-batch/grid/hooks/use-upfront-fee-column-type';
import { useStatusColumnType } from 'routes/secondary-market-batch/grid/utils/status-column-type';
import { RowData } from 'state/workspaces/secondary-market/batch/model';

export function useColumnTypesByOrderType(
  orderType: OrderType,
): Record<string, ColTypeDef<RowData, RowData[keyof RowData]>> {
  const number = useNumberColumnType();
  const customColumns = useMemo(() => ({ number }), [number]);

  const types = useClientSideDefaultColumnTypes<RowData>(customColumns);
  const stringArray = useStringArrayColumnType();
  const percentOrAmount = usePercentOrAmountColumnType();
  const datetimeWithTimezone = useDatetimeWithTimezoneColumnType();
  const semanticDatetimeWithTimezone = useSemanticDatetimeWithTimezoneColumnType();
  const custodian = useCustodianColumnType();
  const labeledOptionList = useLabeledOptionListColumnType(orderType);
  const restrictedDate = useRestrictedDateColumnType();
  const datesArray = useDatesArrayColumnType();
  const salesCounterpart = useSalesCounterpartColumnType();
  const sales = useSalesColumnType();
  const settlements = useSettlementsColumnType();
  const status = useStatusColumnType();
  const upfrontFee = useUpfrontFeeColumnType(types['yesNo']);
  const largeText = useLargeTextColumnType();
  const operationStatus = useOperationStatus(orderType);

  return useMemo(
    () => ({
      ...types,
      stringArray,
      status,
      percentOrAmount,
      datetimeWithTimezone,
      semanticDatetimeWithTimezone,
      custodian,
      labeledOptionList,
      restrictedDate,
      datesArray,
      salesCounterpart,
      sales,
      settlements,
      upfrontFee,
      largeText,
      number,
      operationStatus,
    }),
    [
      types,
      stringArray,
      status,
      percentOrAmount,
      datetimeWithTimezone,
      semanticDatetimeWithTimezone,
      custodian,
      labeledOptionList,
      restrictedDate,
      datesArray,
      salesCounterpart,
      sales,
      settlements,
      upfrontFee,
      largeText,
      number,
      operationStatus,
    ],
  );
}
